/**
 * PartnersPage — Kleda referral-partners beheren (admin-only).
 *
 * Registry (kleda_partners) + kwartaalafrekeningen (partner_commissie_runs).
 * Acties: partner aanmaken/bewerken (incl. marketingvelden + logo — de
 * partnerpagina en shortlink op kleda.nl renderen direct uit de registry,
 * ISR 5 min), dry-run draaien (preview zoals de mail), afrekening versturen,
 * run op betaald zetten.
 *
 * Tussenstand: het lopende kwartaal staat automatisch op de pagina (totaal
 * bovenaan, per partner een 'loopt'-regel boven de afgerekende kwartalen).
 * Zelfde berekening als de afrekening, alleen zonder mail of run-write.
 *
 * Backend: GET/POST/PATCH /api/partners + POST /api/partners/logo
 * + POST /api/partners/commission-run + GET /api/partners/tussenstand.
 */
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { processImageForUpload } from '../lib/image';

interface Partner {
  id: string;
  code: string;
  naam: string;
  email: string;
  commissie_pct: number;
  referrer_codes: string[];
  lead_sources: string[];
  active: boolean;
  website: string | null;
  city: string | null;
  description: string | null;
  intro: string | null;
  logo_url: string | null;
  show_on_kleda: boolean;
}

interface Run {
  id: string;
  partner_id: string;
  periode_label: string;
  periode_start: string;
  periode_eind: string;
  order_count: number;
  omzet_excl_btw: number;
  commissie_pct: number;
  commissie_bedrag: number;
  orders: CommissieOrder[];
  status: 'draft' | 'sent' | 'paid';
  sent_at: string | null;
  paid_at: string | null;
  created_at: string;
  kleda_partners?: { code: string; naam: string };
}

interface CommissieOrder {
  woName: string;
  datum: string;
  naam: string;
  plaats: string;
  omzetExcl: number;
  fallbackDatum: boolean;
}

interface CommissieResult {
  partnerCode: string;
  periodeLabel: string;
  periodeStart: string;
  periodeEind: string;
  orders: CommissieOrder[];
  omzetExclBtw: number;
  commissiePct: number;
  commissieBedrag: number;
}

interface RunOutcome {
  partnerCode: string;
  partnerNaam: string;
  action: string;
  result: CommissieResult;
}

interface Tussenstand {
  quarter: string;
  periode: { start: string; eind: string };
  berekendOp: string;
  cached: boolean;
  outcomes: RunOutcome[];
  totaal: { orderCount: number; omzetExclBtw: number; commissieBedrag: number };
}

async function apiFetch(url: string, init?: RequestInit): Promise<Response> {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error('Niet ingelogd');
  return fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init?.headers ?? {}),
    },
  });
}

function euro(n: number): string {
  return Number(n).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });
}

function nlDatum(iso: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
}

function currentQuarterLabel(): string {
  const now = new Date();
  return `${now.getFullYear()}-Q${Math.floor(now.getMonth() / 3) + 1}`;
}

// Spiegelt de server-side template in kleda-nl src/lib/partners.ts —
// leeg intro-veld → deze zin op de partnerpagina.
function introTemplate(naam: string): string {
  return `Een prachtig kleed gekocht bij ${naam.trim() || '…'}? Wij zorgen dat het schoon en fris blijft.`;
}

// Kleine logo's (PNG/SVG) onbewerkt uploaden zodat transparantie behouden
// blijft; alleen grote bestanden via de JPEG-verkleiner (Vercel ~4,5MB
// request-body-limiet).
const LOGO_DIRECT_MAX_BYTES = 1024 * 1024;

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result);
      resolve(dataUrl.slice(dataUrl.indexOf(',') + 1));
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function uploadLogo(code: string, file: File): Promise<string> {
  const payload =
    file.size <= LOGO_DIRECT_MAX_BYTES
      ? { filename: file.name, contentType: file.type || 'image/png', base64: await fileToBase64(file) }
      : await processImageForUpload(file);
  const resp = await apiFetch('/api/partners/logo', {
    method: 'POST',
    body: JSON.stringify({ code, ...payload }),
  });
  const data = await resp.json();
  if (!resp.ok) throw new Error(data.error ?? `API error ${resp.status}`);
  return data.url as string;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="px-2 py-0.5 text-xs border border-slate-300 rounded text-slate-600 hover:bg-slate-50 shrink-0"
    >
      {copied ? 'Gekopieerd ✓' : 'Kopieer'}
    </button>
  );
}

function PartnerLinksPanel({ code, naam }: { code: string; naam: string }) {
  const liveUrl = `https://kleda.nl/partners/${code}`;
  const shortUrl = `https://kleda.nl/${code}`;
  return (
    <div className="mt-3 border border-emerald-200 bg-emerald-50/60 rounded-lg p-4 space-y-2">
      <p className="text-sm font-semibold text-emerald-900">{naam} staat live op kleda.nl</p>
      {[
        { label: 'Partnerpagina', url: liveUrl },
        { label: 'Shortlink', url: shortUrl },
      ].map(({ label, url }) => (
        <div key={url} className="flex items-center gap-2 text-sm">
          <span className="text-gray-500 w-24 shrink-0">{label}</span>
          <a href={url} target="_blank" rel="noreferrer" className="text-indigo-700 hover:underline truncate">
            {url}
          </a>
          <CopyButton text={url} />
        </div>
      ))}
      <p className="text-xs text-gray-600 pt-1">
        Deel deze link met de partner — verwijzingen via deze pagina tellen automatisch mee in de commissie.
      </p>
    </div>
  );
}

interface MarketingForm {
  website: string;
  city: string;
  description: string;
  intro: string;
  logo_url: string;
  show_on_kleda: boolean;
}

function marketingFormFromPartner(p?: Partner): MarketingForm {
  return {
    website: p?.website ?? '',
    city: p?.city ?? '',
    description: p?.description ?? '',
    intro: p?.intro ?? '',
    logo_url: p?.logo_url ?? '',
    show_on_kleda: p?.show_on_kleda ?? true,
  };
}

interface MarketingFieldsEditorProps {
  code: string;
  naam: string;
  value: MarketingForm;
  onChange: (next: MarketingForm) => void;
  onError: (msg: string) => void;
}

function MarketingFieldsEditor({ code, naam, value, onChange, onError }: MarketingFieldsEditorProps) {
  const [logoBusy, setLogoBusy] = useState(false);

  const handleLogo = async (file: File | undefined) => {
    if (!file) return;
    if (!code.trim()) {
      onError('Vul eerst de partner-code in — die bepaalt de opslaglocatie van het logo');
      return;
    }
    setLogoBusy(true);
    try {
      const url = await uploadLogo(code.trim().toLowerCase(), file);
      onChange({ ...value, logo_url: url });
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Logo-upload mislukt');
    } finally {
      setLogoBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-2 gap-3">
      <input
        value={value.website}
        onChange={(e) => onChange({ ...value, website: e.target.value })}
        placeholder="Website (https://…)"
        className="px-2 py-1.5 border border-slate-300 rounded-md text-sm"
      />
      <input
        value={value.city}
        onChange={(e) => onChange({ ...value, city: e.target.value })}
        placeholder="Plaats"
        className="px-2 py-1.5 border border-slate-300 rounded-md text-sm"
      />
      <input
        value={value.description}
        onChange={(e) => onChange({ ...value, description: e.target.value })}
        placeholder="Korte omschrijving (bv. 'Specialist in handgemaakte kelims')"
        className="col-span-2 px-2 py-1.5 border border-slate-300 rounded-md text-sm"
      />
      <div className="col-span-2">
        <textarea
          value={value.intro}
          onChange={(e) => onChange({ ...value, intro: e.target.value })}
          placeholder={`${introTemplate(naam)} (leeg laten = deze standaardzin)`}
          rows={2}
          className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm"
        />
        <p className="text-xs text-gray-500 mt-1">
          Op de pagina: <span className="italic">“{value.intro.trim() || introTemplate(naam)}”</span>
        </p>
      </div>
      <div className="col-span-2 flex items-center gap-3">
        {value.logo_url && (
          <img src={value.logo_url} alt="Logo" className="h-10 w-auto max-w-[120px] object-contain border border-slate-200 rounded bg-white p-1" />
        )}
        <label className="text-sm text-slate-600">
          <span className="block text-xs font-medium mb-0.5">Logo {logoBusy && '(uploaden…)'}</span>
          <input
            type="file"
            accept="image/*"
            disabled={logoBusy}
            onChange={(e) => {
              handleLogo(e.target.files?.[0]);
              e.target.value = '';
            }}
            className="text-xs"
          />
        </label>
        {value.logo_url && (
          <button
            onClick={() => onChange({ ...value, logo_url: '' })}
            className="text-xs text-gray-400 hover:text-red-600"
          >
            Verwijderen
          </button>
        )}
      </div>
      <label className="col-span-2 flex items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={value.show_on_kleda}
          onChange={(e) => onChange({ ...value, show_on_kleda: e.target.checked })}
          className="rounded border-slate-300"
        />
        Tonen op kleda.nl (partnerpagina + shortlink)
      </label>
    </div>
  );
}

function StatusBadge({ status }: { status: Run['status'] }) {
  const styles: Record<Run['status'], string> = {
    draft: 'bg-gray-100 text-gray-600',
    sent: 'bg-blue-100 text-blue-800',
    paid: 'bg-emerald-100 text-emerald-800',
  };
  const labels: Record<Run['status'], string> = {
    draft: 'Concept',
    sent: 'Verstuurd',
    paid: 'Betaald',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

function PreviewTable({ result, label = 'Preview' }: { result: CommissieResult; label?: string }) {
  return (
    <div className="mt-4 border border-indigo-200 bg-indigo-50/50 rounded-lg p-4">
      <div className="flex items-baseline justify-between mb-3">
        <h4 className="font-semibold text-gray-900 text-sm">
          {label} {result.periodeLabel} ({nlDatum(result.periodeStart)} – {nlDatum(result.periodeEind)})
        </h4>
        <span className="text-sm font-semibold text-indigo-700">
          Commissie {result.commissiePct}%: {euro(result.commissieBedrag)} excl. BTW
        </span>
      </div>
      {result.orders.length === 0 ? (
        <p className="text-sm text-gray-500">Geen afgeleverde orders in deze periode.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 uppercase">
              <th className="py-1.5 pr-3">Afgeleverd</th>
              <th className="py-1.5 pr-3">Order</th>
              <th className="py-1.5 pr-3">Klant</th>
              <th className="py-1.5 pr-3">Plaats</th>
              <th className="py-1.5 text-right">Omzet excl.</th>
            </tr>
          </thead>
          <tbody>
            {result.orders.map((o) => (
              <tr key={o.woName} className="border-t border-indigo-100">
                <td className="py-1.5 pr-3 whitespace-nowrap">
                  {nlDatum(o.datum)}
                  {o.fallbackDatum && (
                    <span className="ml-1 text-amber-600" title="Geen verwerkte terugbreng-rit — datum o.b.v. laatste wijziging">
                      *
                    </span>
                  )}
                </td>
                <td className="py-1.5 pr-3">{o.woName}</td>
                <td className="py-1.5 pr-3">{o.naam}</td>
                <td className="py-1.5 pr-3">{o.plaats}</td>
                <td className="py-1.5 text-right whitespace-nowrap">{euro(o.omzetExcl)}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-indigo-200 font-semibold">
              <td className="py-1.5" colSpan={4}>
                Totaal ({result.orders.length} orders)
              </td>
              <td className="py-1.5 text-right whitespace-nowrap">{euro(result.omzetExclBtw)}</td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

function nlTijd(iso: string): string {
  return new Date(iso).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
}

interface TussenstandPanelProps {
  data: Tussenstand | null;
  busy: boolean;
  error: string | null;
  onRefresh: () => void;
}

/**
 * Lopend kwartaal in één oogopslag: wat hebben de verwezen klanten tot nu toe
 * opgeleverd en wat staat daarmee aan commissie open. Telt alleen afgeleverde
 * orders — hetzelfde criterium als de afrekening, dus geen verrassing achteraf.
 */
function TussenstandPanel({ data, busy, error, onRefresh }: TussenstandPanelProps) {
  const stats = data
    ? [
        { label: 'Omzet via partners', value: euro(data.totaal.omzetExclBtw), hint: 'excl. BTW' },
        { label: 'Commissie', value: euro(data.totaal.commissieBedrag), hint: 'excl. BTW' },
        { label: 'Orders', value: String(data.totaal.orderCount), hint: 'afgeleverd' },
      ]
    : [];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 className="font-semibold text-gray-900">
            Tussenstand {data?.quarter ?? 'dit kwartaal'}
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {data
              ? `${nlDatum(data.periode.start)} t/m vandaag · bijgewerkt ${nlTijd(data.berekendOp)}`
              : 'Lopend kwartaal, alle actieve partners samen'}
          </p>
        </div>
        <button
          onClick={onRefresh}
          disabled={busy}
          className="text-sm text-indigo-600 hover:text-indigo-800 font-medium disabled:opacity-50"
        >
          {busy ? 'Berekenen…' : 'Vernieuwen'}
        </button>
      </div>

      {error ? (
        <p className="text-sm text-amber-700 mt-3">Tussenstand niet beschikbaar: {error}</p>
      ) : busy && !data ? (
        <p className="text-sm text-gray-500 mt-3">Cijfers ophalen uit Salesforce…</p>
      ) : data ? (
        <>
          <div className="grid grid-cols-3 gap-4 mt-4">
            {stats.map((s) => (
              <div key={s.label}>
                <p className="text-xs text-gray-500 uppercase tracking-wide">{s.label}</p>
                <p className="text-2xl font-semibold text-gray-900 mt-0.5">{s.value}</p>
                <p className="text-xs text-gray-400">{s.hint}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-4">
            {data.totaal.orderCount === 0
              ? 'Nog geen commissie dit kwartaal: er is nog geen eerste order van een verwezen klant afgeleverd. Herhaalorders van klanten die al eerder via een partner kwamen tellen niet mee, en orders die nog onderweg zijn schuiven mee naar het kwartaal waarin ze worden afgeleverd.'
              : 'Telt eerste orders van verwezen klanten die dit kwartaal zijn afgeleverd. Herhaalorders tellen niet mee; orders die nog onderweg zijn schuiven mee naar het kwartaal waarin ze worden afgeleverd.'}
          </p>
        </>
      ) : null}
    </div>
  );
}

interface PartnerCardProps {
  partner: Partner;
  runs: Run[];
  tussenstand?: CommissieResult;
  onChanged: () => void;
  onError: (msg: string) => void;
}

function PartnerCard({ partner, runs, tussenstand, onChanged, onError }: PartnerCardProps) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ naam: partner.naam, email: partner.email, pct: String(partner.commissie_pct) });
  const [marketing, setMarketing] = useState<MarketingForm>(() => marketingFormFromPartner(partner));
  const [quarter, setQuarter] = useState(currentQuarterLabel());
  const [preview, setPreview] = useState<CommissieResult | null>(null);
  const [showTussenstand, setShowTussenstand] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  // Zodra het kwartaal is afgerekend is de run-regel de waarheid — dan geen
  // losse tussenstand-regel ernaast.
  const afgerekend = runs.some(
    (r) => r.periode_label === tussenstand?.periodeLabel && (r.status === 'sent' || r.status === 'paid'),
  );
  const lopend = afgerekend ? null : tussenstand ?? null;

  const savePartner = async () => {
    setBusy('save');
    try {
      const resp = await apiFetch(`/api/partners?id=${partner.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          naam: form.naam.trim(),
          email: form.email.trim(),
          commissie_pct: Number(form.pct) || 10,
          website: marketing.website.trim() || null,
          city: marketing.city.trim() || null,
          description: marketing.description.trim() || null,
          intro: marketing.intro.trim() || null,
          logo_url: marketing.logo_url.trim() || null,
          show_on_kleda: marketing.show_on_kleda,
        }),
      });
      if (!resp.ok) throw new Error((await resp.json()).error ?? `API error ${resp.status}`);
      setEditing(false);
      onChanged();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Opslaan mislukt');
    } finally {
      setBusy(null);
    }
  };

  const toggleActive = async () => {
    setBusy('active');
    try {
      const resp = await apiFetch(`/api/partners?id=${partner.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ active: !partner.active }),
      });
      if (!resp.ok) throw new Error((await resp.json()).error ?? `API error ${resp.status}`);
      onChanged();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Wijzigen mislukt');
    } finally {
      setBusy(null);
    }
  };

  const runCommission = async (send: boolean) => {
    if (send && preview) {
      const ok = window.confirm(
        `Afrekening ${preview.periodeLabel} versturen naar ${partner.naam}` +
          `${partner.email ? ` (${partner.email})` : ' (géén e-mailadres — wordt als concept bewaard)'}?\n\n` +
          `${preview.orders.length} orders · ${euro(preview.omzetExclBtw)} excl. BTW · ` +
          `commissie ${euro(preview.commissieBedrag)}`,
      );
      if (!ok) return;
    }
    setBusy(send ? 'send' : 'dryrun');
    try {
      const resp = await apiFetch('/api/partners/commission-run', {
        method: 'POST',
        body: JSON.stringify({ partnerCode: partner.code, quarter, send }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error ?? `API error ${resp.status}`);
      const outcome = (data.outcomes as RunOutcome[])[0];
      if (!outcome) throw new Error('Geen resultaat terug van de berekening');
      if (send) {
        setPreview(null);
        if (outcome.action === 'skipped_already_sent') {
          onError(`${quarter} is al verstuurd voor ${partner.naam} — niet opnieuw verzonden.`);
        }
        onChanged();
      } else {
        setPreview(outcome.result);
      }
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Run mislukt');
    } finally {
      setBusy(null);
    }
  };

  const markPaid = async (run: Run) => {
    setBusy(`paid-${run.id}`);
    try {
      const resp = await apiFetch(`/api/partners?runId=${run.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'paid' }),
      });
      if (!resp.ok) throw new Error((await resp.json()).error ?? `API error ${resp.status}`);
      onChanged();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Bijwerken mislukt');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      {/* Partner-kop + bewerken */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          {editing ? (
            <div className="max-w-2xl space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <input
                  value={form.naam}
                  onChange={(e) => setForm({ ...form, naam: e.target.value })}
                  className="px-2 py-1.5 border border-slate-300 rounded-md text-sm"
                  placeholder="Naam"
                />
                <input
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="px-2 py-1.5 border border-slate-300 rounded-md text-sm"
                  placeholder="E-mailadres"
                />
                <input
                  value={form.pct}
                  onChange={(e) => setForm({ ...form, pct: e.target.value })}
                  className="px-2 py-1.5 border border-slate-300 rounded-md text-sm w-20"
                  placeholder="%"
                  inputMode="decimal"
                />
              </div>
              <MarketingFieldsEditor
                code={partner.code}
                naam={form.naam}
                value={marketing}
                onChange={setMarketing}
                onError={onError}
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={savePartner}
                  disabled={busy === 'save'}
                  className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-sm font-medium disabled:opacity-50"
                >
                  Opslaan
                </button>
                <button onClick={() => setEditing(false)} className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900">
                  Annuleren
                </button>
                <span className="text-xs text-gray-500">Wijzigingen staan binnen 5 minuten op kleda.nl</span>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900">{partner.naam}</h3>
                {!partner.active && (
                  <span className="inline-flex px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500">Inactief</span>
                )}
              </div>
              <p className="text-sm text-gray-500 mt-0.5">
                {partner.email || <span className="text-amber-600">géén e-mailadres</span>}
                {' · '}
                {Number(partner.commissie_pct)}% commissie
                {' · '}
                codes: {[...partner.referrer_codes, ...partner.lead_sources].join(', ') || '—'}
              </p>
              {partner.active && partner.show_on_kleda && (
                <p className="text-sm mt-0.5">
                  <a
                    href={`https://kleda.nl/partners/${partner.code}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-indigo-600 hover:underline"
                  >
                    kleda.nl/partners/{partner.code} ↗
                  </a>
                </p>
              )}
            </>
          )}
        </div>
        {!editing && (
          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={() => {
                setMarketing(marketingFormFromPartner(partner));
                setForm({ naam: partner.naam, email: partner.email, pct: String(partner.commissie_pct) });
                setEditing(true);
              }}
              className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
            >
              Bewerken
            </button>
            <button onClick={toggleActive} disabled={busy === 'active'} className="text-sm text-gray-500 hover:text-gray-800">
              {partner.active ? 'Deactiveren' : 'Activeren'}
            </button>
          </div>
        )}
      </div>

      {/* Dry-run / versturen */}
      <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-gray-100">
        <label className="text-xs font-medium text-slate-600">Kwartaal</label>
        <input
          value={quarter}
          onChange={(e) => setQuarter(e.target.value)}
          className="px-2 py-1.5 border border-slate-200 rounded-md text-sm w-28"
          placeholder="2026-Q3"
        />
        <button
          onClick={() => runCommission(false)}
          disabled={busy !== null}
          className="px-3 py-1.5 border border-indigo-300 text-indigo-700 hover:bg-indigo-50 rounded-md text-sm font-medium disabled:opacity-50"
        >
          {busy === 'dryrun' ? 'Berekenen…' : 'Dry-run'}
        </button>
        <button
          onClick={() => runCommission(true)}
          disabled={busy !== null || !preview}
          title={preview ? undefined : 'Draai eerst een dry-run ter controle'}
          className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-sm font-medium disabled:opacity-50"
        >
          {busy === 'send' ? 'Versturen…' : 'Verstuur afrekening'}
        </button>
      </div>

      {preview && <PreviewTable result={preview} />}

      {/* Runs — lopend kwartaal bovenaan, daaronder de afgerekende periodes */}
      {(runs.length > 0 || lopend) && (
        <div className="mt-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase">
                <th className="py-1.5 pr-3">Periode</th>
                <th className="py-1.5 pr-3 text-right">Orders</th>
                <th className="py-1.5 pr-3 text-right">Omzet excl.</th>
                <th className="py-1.5 pr-3 text-right">Commissie</th>
                <th className="py-1.5 pr-3">Status</th>
                <th className="py-1.5 pr-3">Datum</th>
                <th className="py-1.5"></th>
              </tr>
            </thead>
            <tbody>
              {lopend && (
                <tr className="border-t border-gray-100 bg-indigo-50/40">
                  <td className="py-2 pr-3 font-medium text-gray-900">{lopend.periodeLabel}</td>
                  <td className="py-2 pr-3 text-right">{lopend.orders.length}</td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap">{euro(lopend.omzetExclBtw)}</td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap font-medium">
                    {euro(lopend.commissieBedrag)}
                  </td>
                  <td className="py-2 pr-3">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                      Tussenstand
                    </span>
                  </td>
                  <td className="py-2 pr-3 whitespace-nowrap text-gray-500">loopt</td>
                  <td className="py-2 text-right">
                    {lopend.orders.length > 0 && (
                      <button
                        onClick={() => setShowTussenstand((v) => !v)}
                        className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                      >
                        {showTussenstand ? 'Verberg orders' : 'Bekijk orders'}
                      </button>
                    )}
                  </td>
                </tr>
              )}
              {runs.map((run) => (
                <tr key={run.id} className="border-t border-gray-100">
                  <td className="py-2 pr-3 font-medium text-gray-900">{run.periode_label}</td>
                  <td className="py-2 pr-3 text-right">{run.order_count}</td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap">{euro(run.omzet_excl_btw)}</td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap font-medium">{euro(run.commissie_bedrag)}</td>
                  <td className="py-2 pr-3">
                    <StatusBadge status={run.status} />
                  </td>
                  <td className="py-2 pr-3 whitespace-nowrap text-gray-500">
                    {nlDatum(run.sent_at ?? run.created_at)}
                  </td>
                  <td className="py-2 text-right">
                    {run.status === 'sent' && (
                      <button
                        onClick={() => markPaid(run)}
                        disabled={busy === `paid-${run.id}`}
                        className="text-sm text-emerald-700 hover:text-emerald-900 font-medium disabled:opacity-50"
                      >
                        Markeer betaald
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {lopend && showTussenstand && <PreviewTable result={lopend} label="Tussenstand" />}
    </div>
  );
}

interface NewPartnerFormProps {
  onCreated: () => void;
  onCancel: () => void;
  onError: (msg: string) => void;
}

function NewPartnerForm({ onCreated, onCancel, onError }: NewPartnerFormProps) {
  const [form, setForm] = useState({ code: '', naam: '', email: '', pct: '10', codes: '', sources: '' });
  const [marketing, setMarketing] = useState<MarketingForm>(() => marketingFormFromPartner());
  const [created, setCreated] = useState<{ code: string; naam: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!form.code.trim() || !form.naam.trim()) {
      onError('Code en naam zijn verplicht');
      return;
    }
    setBusy(true);
    try {
      const code = form.code.trim().toLowerCase();
      const codes = form.codes.split(',').map((s) => s.trim()).filter(Boolean);
      const sources = form.sources.split(',').map((s) => s.trim()).filter(Boolean);
      const resp = await apiFetch('/api/partners', {
        method: 'POST',
        body: JSON.stringify({
          code,
          naam: form.naam.trim(),
          email: form.email.trim(),
          commissie_pct: Number(form.pct) || 10,
          // Leeg laten → API default [code]: ?ref + bron-optie werken meteen.
          ...(codes.length > 0 ? { referrer_codes: codes } : {}),
          ...(sources.length > 0 ? { lead_sources: sources } : {}),
          website: marketing.website.trim() || null,
          city: marketing.city.trim() || null,
          description: marketing.description.trim() || null,
          intro: marketing.intro.trim() || null,
          logo_url: marketing.logo_url.trim() || null,
          show_on_kleda: marketing.show_on_kleda,
        }),
      });
      if (!resp.ok) throw new Error((await resp.json()).error ?? `API error ${resp.status}`);
      setCreated({ code, naam: form.naam.trim() });
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Aanmaken mislukt');
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    return (
      <div className="bg-white rounded-xl border border-indigo-200 p-5">
        <h3 className="font-semibold text-gray-900 mb-1">Partner aangemaakt</h3>
        <PartnerLinksPanel code={created.code} naam={created.naam} />
        <button
          onClick={onCreated}
          className="mt-3 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-sm font-medium"
        >
          Sluiten
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-indigo-200 p-5">
      <h3 className="font-semibold text-gray-900 mb-3">Nieuwe partner</h3>
      <div className="max-w-2xl space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Code (slug, bv. tapijthuis)" className="px-2 py-1.5 border border-slate-300 rounded-md text-sm" />
          <input value={form.naam} onChange={(e) => setForm({ ...form, naam: e.target.value })} placeholder="Naam" className="px-2 py-1.5 border border-slate-300 rounded-md text-sm" />
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="E-mailadres" className="px-2 py-1.5 border border-slate-300 rounded-md text-sm" />
          <input value={form.pct} onChange={(e) => setForm({ ...form, pct: e.target.value })} placeholder="Commissie % (10)" className="px-2 py-1.5 border border-slate-300 rounded-md text-sm" inputMode="decimal" />
          <input value={form.codes} onChange={(e) => setForm({ ...form, codes: e.target.value })} placeholder="Referrer-codes (leeg = code)" className="px-2 py-1.5 border border-slate-300 rounded-md text-sm" />
          <input value={form.sources} onChange={(e) => setForm({ ...form, sources: e.target.value })} placeholder="LeadSources (leeg = code)" className="px-2 py-1.5 border border-slate-300 rounded-md text-sm" />
        </div>
        <MarketingFieldsEditor
          code={form.code}
          naam={form.naam}
          value={marketing}
          onChange={setMarketing}
          onError={onError}
        />
      </div>
      <p className="text-xs text-gray-500 mt-2">
        Na aanmaken staat de partnerpagina (kleda.nl/partners/…) en shortlink direct live — geen deploy nodig.
      </p>
      <div className="flex gap-2 mt-3">
        <button onClick={create} disabled={busy} className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-sm font-medium disabled:opacity-50">
          {busy ? 'Aanmaken…' : 'Partner aanmaken'}
        </button>
        <button onClick={onCancel} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900">
          Annuleren
        </button>
      </div>
    </div>
  );
}

export function PartnersPage() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [runs, setRuns] = useState<Run[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [tussenstand, setTussenstand] = useState<Tussenstand | null>(null);
  const [tussenstandBusy, setTussenstandBusy] = useState(true);
  const [tussenstandError, setTussenstandError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const resp = await apiFetch('/api/partners');
      if (!resp.ok) throw new Error(`API error: ${resp.status}`);
      const data = await resp.json();
      setPartners(data.partners ?? []);
      setRuns(data.runs ?? []);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fout bij laden');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Losse fetch: de SF-berekening duurt seconden, de registry laadt meteen.
  // Een mislukte tussenstand blokkeert het beheer niet — vandaar een eigen
  // foutmelding in het paneel in plaats van de pagina-brede balk.
  const loadTussenstand = useCallback(async (refresh = false) => {
    setTussenstandBusy(true);
    try {
      const resp = await apiFetch(`/api/partners/tussenstand${refresh ? '?refresh=1' : ''}`);
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error ?? `API error ${resp.status}`);
      setTussenstand(data);
      setTussenstandError(null);
    } catch (e) {
      setTussenstandError(e instanceof Error ? e.message : 'Berekenen mislukt');
    } finally {
      setTussenstandBusy(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadTussenstand();
  }, [load, loadTussenstand]);

  return (
    <>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className="flex items-start justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Partners</h1>
              <p className="text-gray-500 mt-1">
                Kleda referral-partners — kwartaalafrekeningen (commissie over eerste orders van verwezen klanten)
              </p>
            </div>
            <button
              onClick={() => setShowNew(true)}
              className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-sm font-medium shrink-0"
            >
              + Partner
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start justify-between gap-4">
              <p className="text-red-800 text-sm">{error}</p>
              <button onClick={() => setError(null)} className="text-red-400 hover:text-red-700 text-sm">
                ✕
              </button>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-4">
              {partners.length > 0 && (
                <TussenstandPanel
                  data={tussenstand}
                  busy={tussenstandBusy}
                  error={tussenstandError}
                  onRefresh={() => loadTussenstand(true)}
                />
              )}
              {showNew && (
                <NewPartnerForm
                  onCreated={() => {
                    setShowNew(false);
                    load();
                  }}
                  onCancel={() => setShowNew(false)}
                  onError={setError}
                />
              )}
              {partners.map((p) => (
                <PartnerCard
                  key={p.id}
                  partner={p}
                  runs={runs.filter((r) => r.partner_id === p.id)}
                  tussenstand={tussenstand?.outcomes.find((o) => o.partnerCode === p.code)?.result}
                  onChanged={load}
                  onError={setError}
                />
              ))}
              {partners.length === 0 && !showNew && (
                <p className="text-sm text-gray-500">Nog geen partners in de registry.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
